// components/ProductCard.js
import React, { useState } from 'react';
import { Card, CardContent, CardMedia, Typography, Button, Box, Rating, Chip, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const vibrantColors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', 
    '#FF9F43', '#A29BFE', '#FD79A8', '#00B894',
    '#E17055', '#74B9FF', '#FDCB6E', '#6C5CE7'
];

function ProductCard({ product, index }) {
    const navigate = useNavigate();
    const [addingToCart, setAddingToCart] = useState(false);
    
    const {
        id,
        slug,
        title,
        image,
        price,
        discount_price,
        short_description,
        description,
        average_rating,
        review_count,
        is_on_sale,
        discount_percentage,
        category,
        is_new = false,
        is_bestseller = false,
        stock = 0
    } = product;

    // Generate a consistent color for each product
    const cardColor = vibrantColors[(id || index || 0) % vibrantColors.length];

    // ✅ Navigate to product detail page with proper debugging
    const handleProductClick = () => {
        console.log('🔄 ProductCard clicked:', { id, slug, title });
        
        try {
            if (slug) {
                console.log('📤 Navigating to product with slug:', slug);
                navigate(`/products/${slug}`);
            } else if (id) {
                console.log('📤 Navigating to product with ID:', id);
                navigate(`/products/${id}`);
            } else {
                console.error('❌ No ID or slug found for product:', product);
                alert('Error: Product ID or slug not found');
            }
        } catch (error) {
            console.error('❌ Navigation error:', error);
        }
    };

    // ✅ Handle Add to Cart
    const handleAddToCart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setAddingToCart(true);

        try {
            // Get existing cart from localStorage
            const cart = JSON.parse(localStorage.getItem('cart') || '[]');
            
            // Check if product already in cart
            const existingItem = cart.find(item => item.id === id);
            
            if (existingItem) {
                // If exists, increase quantity
                existingItem.quantity = (existingItem.quantity || 1) + 1;
            } else {
                // If new, add to cart
                cart.push({
                    id: id,
                    title: title || 'Product',
                    price: is_on_sale && discount_price ? parseFloat(discount_price) : parseFloat(price),
                    originalPrice: parseFloat(price),
                    image: image || '',
                    quantity: 1,
                    slug: slug || id
                });
            }

            // Save back to localStorage
            localStorage.setItem('cart', JSON.stringify(cart));
            
            // Dispatch event to update cart count
            window.dispatchEvent(new CustomEvent('cartUpdated', { 
                detail: { cart, itemCount: cart.length } 
            }));

            // Show success message
            alert(`✅ Added "${title || 'Product'}" to cart!`);

        } catch (error) {
            console.error('Error adding to cart:', error);
            alert('⚠️ Could not add to cart. Please try again.');
        } finally {
            setAddingToCart(false);
        }
    };

    // ✅ Handle WhatsApp order
    const handleWhatsAppOrder = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const finalPrice = is_on_sale && discount_price ? discount_price : price;
        const message = `Hello, I would like to order: ${title || 'Product'}\nPrice: $${parseFloat(finalPrice).toFixed(2)}`;
        window.open(`https://wa.me/254700000000?text=${encodeURIComponent(message)}`, '_blank');
    };

    // ✅ Get display price
    const displayPrice = price !== null && price !== undefined ? parseFloat(price) : null;
    const displayDiscountPrice = discount_price !== null && discount_price !== undefined ? parseFloat(discount_price) : null;
    const finalPrice = is_on_sale && displayDiscountPrice ? displayDiscountPrice : displayPrice;

    // ✅ Truncate description for better display in 4-column layout
    const truncateText = (text, maxLength = 55) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Get display description
    const displayDescription = short_description || description || '';

    return (
        <motion.div
            whileHover={{ y: -8, scale: 1.01 }}
            transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
            style={{ height: '100%', cursor: 'pointer' }}
            onClick={handleProductClick}
        >
            <Card 
                sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    transition: 'box-shadow 0.3s, transform 0.3s',
                    '&:hover': {
                        boxShadow: `0 8px 30px ${cardColor}30`,
                        transform: 'translateY(-4px)',
                    },
                    border: `1px solid ${cardColor}15`
                }}
            >
                {/* ✅ Decorative top bar */}
                <Box
                    sx={{
                        height: 3,
                        background: `linear-gradient(90deg, ${cardColor}, ${cardColor}CC)`,
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 10
                    }}
                />

                {/* ✅ Badges - Smaller for 4-column layout */}
                <Box sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    left: 8, 
                    zIndex: 5, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 0.5 
                }}>
                    {is_new && (
                        <Chip
                            label="NEW"
                            size="small"
                            sx={{
                                bgcolor: '#00B894',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.55rem',
                                height: 18,
                                '& .MuiChip-label': { px: 0.8, py: 0 }
                            }}
                        />
                    )}
                    {is_bestseller && (
                        <Chip
                            label="⭐ BEST"
                            size="small"
                            sx={{
                                bgcolor: '#FF9F43',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.55rem',
                                height: 18,
                                '& .MuiChip-label': { px: 0.8, py: 0 }
                            }}
                        />
                    )}
                    {is_on_sale && (
                        <Chip
                            label={`-${discount_percentage || 20}%`}
                            size="small"
                            sx={{
                                bgcolor: '#FF6B6B',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.55rem',
                                height: 18,
                                '& .MuiChip-label': { px: 0.8, py: 0 }
                            }}
                        />
                    )}
                    {stock === 0 && (
                        <Chip
                            label="OUT"
                            size="small"
                            sx={{
                                bgcolor: '#757575',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.55rem',
                                height: 18,
                                '& .MuiChip-label': { px: 0.8, py: 0 }
                            }}
                        />
                    )}
                </Box>

                {/* ✅ Wishlist Button - Smaller */}
                <IconButton
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'white',
                        zIndex: 5,
                        '&:hover': { bgcolor: 'white', transform: 'scale(1.1)' },
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        width: 28,
                        height: 28
                    }}
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                >
                    <FavoriteBorderIcon sx={{ color: '#FF6B6B', fontSize: 16 }} />
                </IconButton>

                {/* ✅ Product Image - Smaller height for 4 columns */}
                <Box 
                    sx={{ 
                        position: 'relative', 
                        pt: '75%', 
                        bgcolor: `${cardColor}08`,
                        transition: 'transform 0.3s',
                        '&:hover': {
                            transform: 'scale(1.02)'
                        }
                    }}
                >
                    <CardMedia
                        component="img"
                        image={image || `https://via.placeholder.com/300x300/${cardColor.replace('#', '')}/ffffff?text=${title ? title.substring(0, 8) : 'Product'}`}
                        alt={title || 'Product'}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            p: 1.5,
                        }}
                    />
                </Box>

                {/* ✅ Content - Compact for 4 columns */}
                <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
                    {/* Category - Smaller */}
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: cardColor, 
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: '0.6rem'
                        }}
                    >
                        {category || 'Health & Wellness'}
                    </Typography>

                    {/* Title - Compact */}
                    <Typography 
                        gutterBottom 
                        variant="subtitle1"
                        sx={{ 
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            minHeight: 40,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            color: 'inherit',
                            textDecoration: 'none',
                            lineHeight: 1.3,
                            mt: 0.3,
                            mb: 0.5,
                            '&:hover': { color: cardColor }
                        }}
                    >
                        {title || 'Product'}
                    </Typography>

                    {/* Rating - Smaller */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Rating 
                            value={average_rating || 4.5} 
                            readOnly 
                            size="small" 
                            sx={{ color: '#FFB800', fontSize: '0.8rem' }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                            ({review_count || 0})
                        </Typography>
                    </Box>

                    {/* Description - Wrapped and truncated for 4 columns */}
                    <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                            mb: 1,
                            minHeight: 32,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            fontSize: '0.7rem',
                            lineHeight: 1.3
                        }}
                    >
                        {truncateText(displayDescription, 50)}
                    </Typography>

                    {/* Price - Compact */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {finalPrice !== null && finalPrice !== undefined ? (
                            <>
                                {is_on_sale && displayPrice ? (
                                    <>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF6B6B', fontSize: '0.95rem' }}>
                                            ${finalPrice.toFixed(2)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through', fontSize: '0.65rem' }}>
                                            ${displayPrice.toFixed(2)}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2D3436', fontSize: '0.95rem' }}>
                                        ${finalPrice.toFixed(2)}
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#FF6B6B', fontSize: '0.7rem' }}>
                                Contact for Price
                            </Typography>
                        )}
                    </Box>

                    {/* ✅ Action Buttons - Compact for 4 columns */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {/* Add to Cart Button */}
                        <Button
                            variant="contained"
                            fullWidth
                            size="small"
                            startIcon={<ShoppingCartIcon sx={{ fontSize: 14 }} />}
                            onClick={handleAddToCart}
                            disabled={addingToCart || stock === 0}
                            sx={{
                                bgcolor: stock === 0 ? '#ccc' : cardColor,
                                color: 'white',
                                fontWeight: 'bold',
                                borderRadius: 1.5,
                                textTransform: 'none',
                                fontSize: '0.7rem',
                                py: 0.6,
                                minHeight: 30,
                                '&:hover': {
                                    bgcolor: stock === 0 ? '#ccc' : cardColor,
                                    opacity: 0.9,
                                },
                                '&:disabled': {
                                    bgcolor: '#e0e0e0',
                                    color: '#999'
                                }
                            }}
                        >
                            {stock === 0 ? 'Out of Stock' : (addingToCart ? 'Adding...' : 'Add to Cart')}
                        </Button>

                        {/* WhatsApp + Quick View Row */}
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<WhatsAppIcon sx={{ fontSize: 12 }} />}
                                onClick={handleWhatsAppOrder}
                                sx={{
                                    flex: 1,
                                    borderColor: '#25D366',
                                    color: '#25D366',
                                    fontWeight: 'bold',
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontSize: '0.6rem',
                                    py: 0.4,
                                    minHeight: 26,
                                    '&:hover': {
                                        borderColor: '#1DA851',
                                        bgcolor: '#25D36610',
                                    }
                                }}
                            >
                                WhatsApp
                            </Button>
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<VisibilityIcon sx={{ fontSize: 12 }} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('👁️ Quick View clicked for product:', { id, slug, title });
                                    if (id) {
                                        navigate(`/products/${id}`);
                                    } else if (slug) {
                                        navigate(`/products/${slug}`);
                                    }
                                }}
                                sx={{
                                    flex: 1,
                                    color: 'text.secondary',
                                    fontSize: '0.6rem',
                                    textTransform: 'none',
                                    py: 0.4,
                                    minHeight: 26,
                                    '&:hover': {
                                        color: cardColor,
                                        bgcolor: 'transparent'
                                    }
                                }}
                            >
                                View
                            </Button>
                        </Box>
                    </Box>
                </CardContent>

                {/* ✅ Decorative bottom bar */}
                <Box
                    sx={{
                        height: 2,
                        background: `linear-gradient(90deg, ${cardColor}30, ${cardColor}, ${cardColor}30)`,
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0
                    }}
                />
            </Card>
        </motion.div>
    );
}

export default ProductCard;